import { useGetLocale, useTranslate } from '@refinedev/core';
import { Card, Flex, Tabs, Tag, Typography, theme } from 'antd';
import type { ContentPublisherOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { countryName } from '@/utils/countries';

const { Text, Title } = Typography;

interface PublisherShowHeaderProps {
  record: ContentPublisherOut;
  tabs: readonly string[];
  activeTab: string;
  onTabChange: (key: string) => void;
}

export const PublisherShowHeader = ({
  record,
  tabs,
  activeTab,
  onTabChange,
}: PublisherShowHeaderProps) => {
  const t = useTranslate();
  const locale = useGetLocale()() ?? 'pl';
  const { token } = theme.useToken();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginSM} align="baseline" wrap>
        {/* Long names wrap instead of pushing the tabs bar out. */}
        <Title
          level={3}
          style={{
            margin: 0,
            fontSize: token.fontSizeHeading3,
            overflowWrap: 'anywhere',
          }}
        >
          {record.name}
        </Title>
        <Tag>{record.provider}</Tag>
      </Flex>

      <Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginXXS }}
      >
        {`ID ${record.id} · ${record.external_id}`}
      </Text>

      <Flex
        gap={token.marginXL + token.margin}
        style={{ marginTop: token.marginLG }}
        wrap
      >
        <StatCell
          label={t('content_catalog.publishers.fields.has_active_contract')}
        >
          {record.has_active_contract ? (
            <Tag color="green">
              {t('content_catalog.publishers.contract.active')}
            </Tag>
          ) : (
            <Tag>{t('content_catalog.publishers.contract.none')}</Tag>
          )}
        </StatCell>
        <StatCell label={t('content_catalog.publishers.fields.country')}>
          {orDash(
            record.country_code && countryName(record.country_code, locale)
          )}
        </StatCell>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={tabs.map((key) => ({
          key,
          label: t(`content_catalog.publishers.tabs.${key}`),
        }))}
      />
    </Card>
  );
};
